/**
 * Pathway domain <-> FHIR `PlanDefinition` mapper (PROD-642 Sprint 2.2).
 *
 * The Pathway Studio in-memory model is `EditablePathway` from
 * `src/state/pathway.store.ts`. The FHIR canonical shape is `PlanDefinition`
 * with each pathway node modelled as a top-level `action`. The mapping
 * preserves enough information to round-trip `EditablePathway -> PlanDefinition
 * -> EditablePathway` without loss for the fields the editor cares about
 * (id, label, nodeType, requiresConfirmation, stateMachine, linkedNbaIds,
 * labBasketItems, description) plus the edge graph.
 *
 * Failure mode: every parse + assertion fails loudly with a descriptive
 * error so a corrupted PlanDefinition never silently coerces to an
 * empty pathway.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  EditablePathway,
  FormQuestion,
  FormQuestionType,
  PathwayEdge,
  PathwayNode,
  PathwayRule,
  RuleOperator,
} from '../../state/pathway-types';
import {
  FORM_QUESTION_TYPES,
  RULE_OPERATORS,
  RULE_RISK_TIERS,
} from '../../state/pathway-types';
import type { Questionnaire, QuestionnaireItem } from '../fhir/types';
import {
  PATHWAY_IDS,
  type PathwayId,
  pathwayIdFor,
} from '../../state/pathway-ids';
import type {
  CodeableConcept,
  FhirExtension,
  PlanDefinition,
  PlanDefinitionAction,
} from '../fhir/types';
import { TENANT_EXTENSION_URL } from '../fhir/types';
import {
  PATHWAY_NODE_TYPES,
  type PathwayNodeType,
} from '../pathway-layout';

/** Extension URL namespace for the CPS pathway domain mapping. */
const NS = 'http://scrips.com/fhir/StructureDefinition/cps';

export const PATHWAY_NODE_TYPE_EXTENSION_URL = `${NS}-node-type`;
export const PATHWAY_NODE_LABEL_EXTENSION_URL = `${NS}-node-label`;
export const PATHWAY_NODE_DESCRIPTION_EXTENSION_URL = `${NS}-node-description`;
export const PATHWAY_NODE_REQUIRES_CONFIRMATION_EXTENSION_URL = `${NS}-node-requires-confirmation`;
export const PATHWAY_NODE_STATE_MACHINE_EXTENSION_URL = `${NS}-node-state-machine`;
export const PATHWAY_NODE_LINKED_NBAS_EXTENSION_URL = `${NS}-node-linked-nbas`;
export const PATHWAY_NODE_LAB_BASKET_EXTENSION_URL = `${NS}-node-lab-basket`;
export const PATHWAY_EDGES_EXTENSION_URL = `${NS}-pathway-edges`;
export const PATHWAY_ID_EXTENSION_URL = `${NS}-pathway-id`;
// Sprint 4.3.7-fix-2: hierarchy + step-pill fields preserved across the
// PlanDefinition round-trip so the canonical fixtures keep their Awell
// 3-tier shape after `loadPathway` resolves through the FHIR client.
export const PATHWAY_NODE_PARENT_EXTENSION_URL = `${NS}-node-parent`;
export const PATHWAY_NODE_CATEGORY_EXTENSION_URL = `${NS}-node-category`;
export const PATHWAY_NODE_TRIGGER_TYPE_EXTENSION_URL = `${NS}-node-trigger-type`;
export const PATHWAY_NODE_TRIGGER_FILTER_EXTENSION_URL = `${NS}-node-trigger-filter`;
export const PATHWAY_NODE_IS_DECISION_EXTENSION_URL = `${NS}-node-is-decision`;
export const PATHWAY_NODE_STEP_CATEGORY_EXTENSION_URL = `${NS}-node-step-category`;
export const PATHWAY_NODE_STEP_CATEGORY_LABEL_EXTENSION_URL = `${NS}-node-step-category-label`;
export const PATHWAY_NODE_PROGRAM_DURATION_EXTENSION_URL = `${NS}-node-program-duration`;
export const PATHWAY_NODE_ASSIGNED_STAKEHOLDER_EXTENSION_URL = `${NS}-node-assigned-stakeholder`;
// Sprint 4.3.9: form-builder fields. `actionType` discriminates the six
// native activity types (form / calculation / message / api_call /
// reminder / checklist) and `formQuestions` carries the authored
// Questionnaire.item list as a JSON-encoded extension. The mapper also
// emits a sibling FHIR `Questionnaire` representation inline on the
// action via `definitionCanonical` so a Questionnaire round-trip is
// available without requiring the backend to read CPS extensions.
export const PATHWAY_NODE_ACTION_TYPE_EXTENSION_URL = `${NS}-node-action-type`;
export const PATHWAY_NODE_FORM_QUESTIONS_EXTENSION_URL = `${NS}-node-form-questions`;
// Sprint 4.3.10: rules editor. Transition rules attach to step nodes
// and travel through `PlanDefinition.action.condition` (kind:
// `applicability`); form-question display rules attach to a question
// and travel through `Questionnaire.item.enableWhen` plus a CPS rule
// extension that carries the rule object verbatim. The verbatim
// extension exists so the round-trip is lossless for operators FHIR
// does not natively support (in / not-in) and so the optional
// `riskTier` flag survives end-to-end (it has no native FHIR home).
//
// Encoding choice: we serialize the entire `PathwayRule` object as a
// JSON valueString on the extension. This is simpler than building a
// FHIRPath expression for every operator, and the round-trip is
// byte-stable.
export const PATHWAY_RULE_EXTENSION_URL = `${NS}-rule`;
export const PATHWAY_RULE_RISK_TIER_EXTENSION_URL = `${NS}-rule-risk-tier`;

const TYPE_CODING_SYSTEM = `${NS}-pathway-type`;

/**
 * Sprint 4.3.10: rule operator -> FHIR R4 `Questionnaire.item.enableWhen
 * .operator` mapping. Operators FHIR does not natively support fall back
 * to `null` and the encoder relies on the verbatim CPS extension for
 * lossless round-trip.
 *
 * FHIR R4 native enableWhen operators per
 * https://hl7.org/fhir/R4/valueset-questionnaire-enable-operator.html:
 *   `exists | = | != | > | < | >= | <=`
 */
const RULE_OPERATOR_TO_FHIR_ENABLE_WHEN: Record<RuleOperator, string | null> = {
  equals: '=',
  'not-equals': '!=',
  'greater-than': '>',
  'less-than': '<',
  'greater-than-or-equals': '>=',
  'less-than-or-equals': '<=',
  in: null,
  'not-in': null,
  'is-empty': 'exists',
  'is-not-empty': 'exists',
};

/**
 * Sprint 4.3.10: build a human-readable FHIRPath-style expression from a
 * rule. The expression is purely informational on the FHIR side; the
 * authoritative round-trip lives in the CPS rule extension. We emit it
 * on `condition.expression.expression` so an operator browsing the FHIR
 * resource sees the rule intent at a glance.
 */
function ruleToFhirPathString(rule: PathwayRule): string {
  const operand = rule.dataPointDefinitionId;
  switch (rule.operator) {
    case 'is-empty':
      return `${operand}.empty()`;
    case 'is-not-empty':
      return `${operand}.exists()`;
    case 'in':
    case 'not-in': {
      const arr = Array.isArray(rule.value) ? rule.value : [];
      const inverse = rule.operator === 'not-in' ? 'not()' : '';
      const list = arr.map((v) => JSON.stringify(v)).join(', ');
      return `(${operand} in (${list}))${inverse}`;
    }
    default: {
      const fhirOp = RULE_OPERATOR_TO_FHIR_ENABLE_WHEN[rule.operator] ?? '=';
      const v =
        typeof rule.value === 'number'
          ? String(rule.value)
          : JSON.stringify(rule.value ?? '');
      return `${operand} ${fhirOp} ${v}`;
    }
  }
}

/**
 * Sprint 4.3.10: validate a decoded rule object. Throws when any field
 * is outside its closed vocabulary so a corrupted payload fails loudly
 * instead of silently coercing.
 */
function assertValidRule(rule: PathwayRule, contextLabel: string): void {
  if (typeof rule.id !== 'string' || rule.id.length === 0) {
    throw new Error(`${contextLabel}: rule.id missing or not a string`);
  }
  if (typeof rule.name !== 'string') {
    throw new Error(`${contextLabel}: rule.name missing or not a string`);
  }
  if (
    typeof rule.dataPointDefinitionId !== 'string' ||
    rule.dataPointDefinitionId.length === 0
  ) {
    throw new Error(
      `${contextLabel}: rule.dataPointDefinitionId missing or empty`,
    );
  }
  if (!(RULE_OPERATORS as readonly string[]).includes(rule.operator)) {
    throw new Error(
      `${contextLabel}: rule.operator "${rule.operator}" outside closed vocabulary`,
    );
  }
  if (
    rule.riskTier !== undefined &&
    !(RULE_RISK_TIERS as readonly string[]).includes(rule.riskTier)
  ) {
    throw new Error(
      `${contextLabel}: rule.riskTier "${rule.riskTier}" outside closed vocabulary`,
    );
  }
}

/**
 * Sprint 4.3.10: build a `PlanDefinition.action.condition` payload for a
 * step transition rule. The structured CPS extension carrying the rule
 * verbatim travels alongside the FHIR-canonical `condition` so a reader
 * can pick whichever side is convenient.
 */
export function ruleToPlanDefinitionCondition(rule: PathwayRule): unknown {
  assertValidRule(rule, 'ruleToPlanDefinitionCondition');
  const ruleExt: FhirExtension = {
    url: PATHWAY_RULE_EXTENSION_URL,
    valueString: JSON.stringify(rule),
  };
  return {
    kind: 'applicability',
    expression: {
      language: 'text/fhirpath',
      expression: ruleToFhirPathString(rule),
    },
    extension: [ruleExt],
  };
}

/**
 * Sprint 4.3.10: parse a `PlanDefinition.action.condition` payload back
 * into the domain `PathwayRule`. The CPS rule extension is the
 * authoritative source. Throws when the extension is missing or its
 * payload is corrupt.
 */
export function planDefinitionConditionToRule(condition: unknown): PathwayRule {
  const carrier = condition as { extension?: FhirExtension[] } | undefined;
  const ext = carrier?.extension ?? [];
  const ruleExt = ext.find((e) => e.url === PATHWAY_RULE_EXTENSION_URL);
  if (!ruleExt?.valueString) {
    throw new Error(
      'planDefinitionConditionToRule: missing CPS rule extension',
    );
  }
  let parsed: PathwayRule;
  try {
    parsed = JSON.parse(ruleExt.valueString) as PathwayRule;
  } catch (err) {
    throw new Error(
      `planDefinitionConditionToRule: rule JSON parse failed: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
  assertValidRule(parsed, 'planDefinitionConditionToRule');
  return parsed;
}

/**
 * Sprint 4.3.10: build a FHIR R4 `Questionnaire.item.enableWhen` entry
 * for a form-question display rule. The native FHIR vocabulary covers
 * 6 of the 10 operators; the remaining 4 (in / not-in / is-empty /
 * is-not-empty) attach a CPS extension on the enableWhen entry that
 * carries the verbatim rule. The structured extension is also attached
 * so the round-trip is byte-stable for every operator.
 */
export function ruleToQuestionnaireEnableWhen(rule: PathwayRule): unknown {
  assertValidRule(rule, 'ruleToQuestionnaireEnableWhen');
  const ruleExt: FhirExtension = {
    url: PATHWAY_RULE_EXTENSION_URL,
    valueString: JSON.stringify(rule),
  };
  const fhirOperator = RULE_OPERATOR_TO_FHIR_ENABLE_WHEN[rule.operator];
  const entry: Record<string, unknown> = {
    question: rule.dataPointDefinitionId,
    operator: fhirOperator ?? '=',
    extension: [ruleExt],
  };
  // Encode value into a typed `answer*` field. FHIR enableWhen requires
  // exactly one answer*. For operators FHIR does not natively support,
  // a placeholder answerString preserves shape.
  if (rule.operator === 'is-empty') {
    entry.operator = 'exists';
    entry.answerBoolean = false;
  } else if (rule.operator === 'is-not-empty') {
    entry.operator = 'exists';
    entry.answerBoolean = true;
  } else if (rule.operator === 'in' || rule.operator === 'not-in') {
    const arr = Array.isArray(rule.value) ? rule.value : [];
    entry.answerString = arr.join(',');
  } else if (typeof rule.value === 'number') {
    entry.answerDecimal = rule.value;
  } else {
    entry.answerString = String(rule.value ?? '');
  }
  return entry;
}

/**
 * Sprint 4.3.10: parse a FHIR `Questionnaire.item.enableWhen` entry back
 * into the domain `PathwayRule`. The CPS rule extension is the
 * authoritative source so the inverse mapping is byte-stable for every
 * operator regardless of FHIR native support.
 */
export function questionnaireEnableWhenToRule(entry: unknown): PathwayRule {
  const carrier = entry as { extension?: FhirExtension[] } | undefined;
  const ext = carrier?.extension ?? [];
  const ruleExt = ext.find((e) => e.url === PATHWAY_RULE_EXTENSION_URL);
  if (!ruleExt?.valueString) {
    throw new Error(
      'questionnaireEnableWhenToRule: missing CPS rule extension',
    );
  }
  let parsed: PathwayRule;
  try {
    parsed = JSON.parse(ruleExt.valueString) as PathwayRule;
  } catch (err) {
    throw new Error(
      `questionnaireEnableWhenToRule: rule JSON parse failed: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
  assertValidRule(parsed, 'questionnaireEnableWhenToRule');
  return parsed;
}

/**
 * Sprint 4.3.9: domain `FormQuestionType` -> FHIR
 * `QuestionnaireItem.type` mapping. Closed both ways so a corrupt
 * payload throws loudly instead of silently coercing.
 */
const FORM_QUESTION_TO_FHIR_ITEM_TYPE: Record<
  FormQuestionType,
  QuestionnaireItem['type']
> = {
  text: 'string',
  number: 'decimal',
  date: 'date',
  single_choice: 'choice',
  multiple_choice: 'choice',
  scale: 'integer',
};

const FHIR_ITEM_TYPE_TO_FORM_QUESTION_TYPE: Partial<
  Record<QuestionnaireItem['type'], FormQuestionType>
> = {
  string: 'text',
  text: 'text',
  decimal: 'number',
  date: 'date',
  // `choice` is ambiguous (single vs multiple) so the mapper consults
  // the `repeats` flag at decode time.
  integer: 'scale',
};

/**
 * Sprint 4.3.9: convert a domain `FormQuestion` into a FHIR R4
 * `QuestionnaireItem`. Choice options land on `code` codings and on a
 * domain extension so the round-trip is byte-stable. The optional
 * `dataPointDefinitionId` lands as `definition` per the FHIR
 * `Questionnaire.item.definition` field semantics (a canonical URL to
 * the `ObservationDefinition`).
 */
export function formQuestionToQuestionnaireItem(
  q: FormQuestion,
): QuestionnaireItem {
  const item: QuestionnaireItem = {
    linkId: q.id,
    text: q.text,
    type: FORM_QUESTION_TO_FHIR_ITEM_TYPE[q.type],
    required: q.required,
  };
  if (q.type === 'multiple_choice') {
    item.repeats = true;
  }
  if (q.options !== undefined && q.options.length > 0) {
    item.code = q.options.map((opt) => ({
      system: `${NS}-question-option`,
      code: opt.value,
      display: opt.label,
    }));
  }
  if (q.dataPointDefinitionId !== undefined) {
    (item as unknown as Record<string, unknown>).definition =
      `urn:cps:data-point-definition:${q.dataPointDefinitionId}`;
  }
  const itemExt: FhirExtension[] = [];
  if (q.scaleMin !== undefined) {
    itemExt.push({
      url: `${NS}-question-scale-min`,
      valueInteger: q.scaleMin,
    });
  }
  if (q.scaleMax !== undefined) {
    itemExt.push({
      url: `${NS}-question-scale-max`,
      valueInteger: q.scaleMax,
    });
  }
  // Sprint 4.3.10: form-question enableWhen rule. The CPS extension on
  // the enableWhen entry preserves the rule verbatim so operators FHIR
  // does not natively support survive the round-trip.
  if (q.enableWhenRule !== undefined) {
    const enableWhen = ruleToQuestionnaireEnableWhen(q.enableWhenRule);
    (item as unknown as { enableWhen?: unknown[] }).enableWhen = [enableWhen];
  }
  if (itemExt.length > 0) {
    (item as unknown as { extension?: FhirExtension[] }).extension = itemExt;
  }
  return item;
}

/**
 * Sprint 4.3.9: inverse mapping `QuestionnaireItem` -> `FormQuestion`.
 * Throws on unknown `type` so a corrupt payload fails loudly.
 */
export function questionnaireItemToFormQuestion(
  item: QuestionnaireItem,
): FormQuestion {
  const baseType = FHIR_ITEM_TYPE_TO_FORM_QUESTION_TYPE[item.type];
  let type: FormQuestionType;
  if (item.type === 'choice') {
    type = item.repeats === true ? 'multiple_choice' : 'single_choice';
  } else if (baseType !== undefined) {
    type = baseType;
  } else {
    throw new Error(
      `QuestionnaireItem "${item.linkId}" has unsupported type "${item.type}"`,
    );
  }
  if (!(FORM_QUESTION_TYPES as readonly string[]).includes(type)) {
    throw new Error(
      `QuestionnaireItem "${item.linkId}" decoded to unsupported question type "${type}"`,
    );
  }
  const question: FormQuestion = {
    id: item.linkId,
    text: item.text ?? '',
    type,
    required: item.required ?? false,
  };
  if (item.code !== undefined && item.code.length > 0) {
    question.options = item.code.map((c) => ({
      value: c.code ?? '',
      label: c.display ?? c.code ?? '',
    }));
  }
  const definition = (item as unknown as Record<string, unknown>).definition;
  if (typeof definition === 'string') {
    const prefix = 'urn:cps:data-point-definition:';
    question.dataPointDefinitionId = definition.startsWith(prefix)
      ? definition.slice(prefix.length)
      : definition;
  }
  const ext = (item as unknown as { extension?: FhirExtension[] }).extension;
  if (Array.isArray(ext)) {
    const scaleMinExt = ext.find((e) => e.url === `${NS}-question-scale-min`);
    if (scaleMinExt?.valueInteger !== undefined) {
      question.scaleMin = scaleMinExt.valueInteger;
    }
    const scaleMaxExt = ext.find((e) => e.url === `${NS}-question-scale-max`);
    if (scaleMaxExt?.valueInteger !== undefined) {
      question.scaleMax = scaleMaxExt.valueInteger;
    }
  }
  // Sprint 4.3.10: decode the enableWhen rule when present. Only the
  // first enableWhen entry is consulted because the editor authors
  // single-condition rules per sprint scope.
  const enableWhen = (item as unknown as { enableWhen?: unknown[] })
    .enableWhen;
  if (Array.isArray(enableWhen) && enableWhen.length > 0) {
    try {
      question.enableWhenRule = questionnaireEnableWhenToRule(enableWhen[0]);
    } catch (err) {
      throw new Error(
        `QuestionnaireItem "${item.linkId}" enableWhen parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  return question;
}

/**
 * Sprint 4.3.9: build a FHIR R4 `Questionnaire` from a form-type action
 * node. The resource id mirrors the node id so a publish flow can pin
 * the Questionnaire by canonical URL `urn:cps:form:<node-id>` (the
 * Questionnaire is a SHARED component per the vault note
 * `shared-vs-non-shared-components.md`).
 */
export function formActionNodeToQuestionnaire(
  node: PathwayNode,
  tenantId: string,
): Questionnaire {
  if (node.nodeType !== 'action' || node.actionType !== 'form') {
    throw new Error(
      `formActionNodeToQuestionnaire: node "${node.id}" is not a form action`,
    );
  }
  const questions = node.formQuestions ?? [];
  return {
    resourceType: 'Questionnaire',
    id: `form-${node.id}`,
    url: `urn:cps:form:${node.id}`,
    name: node.label,
    title: node.label,
    status: 'active',
    item: questions.map(formQuestionToQuestionnaireItem),
    extension: [
      { url: TENANT_EXTENSION_URL, valueString: tenantId },
      { url: `${NS}-form-source-action-id`, valueString: node.id },
    ],
  };
}

/**
 * Sprint 4.3.9: parse a FHIR R4 `Questionnaire` back into the domain
 * (form-type action) shape. Used by tests + the inverse round-trip.
 */
export function questionnaireToFormQuestions(
  resource: Questionnaire,
): FormQuestion[] {
  if (resource.resourceType !== 'Questionnaire') {
    throw new Error(
      `questionnaireToFormQuestions: expected resourceType "Questionnaire", got "${resource.resourceType}"`,
    );
  }
  if (!Array.isArray(resource.item)) return [];
  return resource.item.map(questionnaireItemToFormQuestion);
}

/**
 * Build a `PlanDefinition.action` entry for a pathway node. The node id
 * lands on `definitionCanonical` so the FHIR resource carries an explicit
 * reference to the action, and the node's free-form fields land on
 * extensions so the round-trip is byte-stable.
 */
function nodeToAction(node: PathwayNode): PlanDefinitionAction {
  const ext: FhirExtension[] = [
    { url: PATHWAY_NODE_TYPE_EXTENSION_URL, valueCode: node.nodeType },
    { url: PATHWAY_NODE_LABEL_EXTENSION_URL, valueString: node.label },
  ];
  if (node.description !== undefined) {
    ext.push({
      url: PATHWAY_NODE_DESCRIPTION_EXTENSION_URL,
      valueString: node.description,
    });
  }
  if (node.requiresConfirmation !== undefined) {
    ext.push({
      url: PATHWAY_NODE_REQUIRES_CONFIRMATION_EXTENSION_URL,
      valueBoolean: node.requiresConfirmation,
    });
  }
  if (node.stateMachine !== undefined) {
    ext.push({
      url: PATHWAY_NODE_STATE_MACHINE_EXTENSION_URL,
      valueString: node.stateMachine,
    });
  }
  if (node.linkedNbaIds !== undefined) {
    ext.push({
      url: PATHWAY_NODE_LINKED_NBAS_EXTENSION_URL,
      valueString: JSON.stringify(node.linkedNbaIds),
    });
  }
  if (node.labBasketItems !== undefined) {
    ext.push({
      url: PATHWAY_NODE_LAB_BASKET_EXTENSION_URL,
      valueString: JSON.stringify(node.labBasketItems),
    });
  }
  // Sprint 4.3.7-fix-2: hierarchy + step-pill round-trip extensions.
  if (node.parentNodeId !== undefined) {
    ext.push({
      url: PATHWAY_NODE_PARENT_EXTENSION_URL,
      valueString: node.parentNodeId,
    });
  }
  if (node.category !== undefined) {
    ext.push({
      url: PATHWAY_NODE_CATEGORY_EXTENSION_URL,
      valueCode: node.category,
    });
  }
  if (node.triggerType !== undefined) {
    ext.push({
      url: PATHWAY_NODE_TRIGGER_TYPE_EXTENSION_URL,
      valueString: node.triggerType,
    });
  }
  if (node.triggerFilter !== undefined) {
    ext.push({
      url: PATHWAY_NODE_TRIGGER_FILTER_EXTENSION_URL,
      valueString: node.triggerFilter,
    });
  }
  if (node.isDecision !== undefined) {
    ext.push({
      url: PATHWAY_NODE_IS_DECISION_EXTENSION_URL,
      valueBoolean: node.isDecision,
    });
  }
  if (node.stepCategory !== undefined) {
    ext.push({
      url: PATHWAY_NODE_STEP_CATEGORY_EXTENSION_URL,
      valueCode: node.stepCategory,
    });
  }
  if (node.stepCategoryLabel !== undefined) {
    ext.push({
      url: PATHWAY_NODE_STEP_CATEGORY_LABEL_EXTENSION_URL,
      valueString: node.stepCategoryLabel,
    });
  }
  if (node.programDuration !== undefined) {
    ext.push({
      url: PATHWAY_NODE_PROGRAM_DURATION_EXTENSION_URL,
      valueString: node.programDuration,
    });
  }
  if (node.assignedStakeholderId !== undefined) {
    ext.push({
      url: PATHWAY_NODE_ASSIGNED_STAKEHOLDER_EXTENSION_URL,
      valueString: node.assignedStakeholderId,
    });
  }
  // Sprint 4.3.9: native action discriminator + form questions.
  if (node.actionType !== undefined) {
    ext.push({
      url: PATHWAY_NODE_ACTION_TYPE_EXTENSION_URL,
      valueCode: node.actionType,
    });
  }
  if (node.formQuestions !== undefined) {
    ext.push({
      url: PATHWAY_NODE_FORM_QUESTIONS_EXTENSION_URL,
      valueString: JSON.stringify(node.formQuestions),
    });
  }
  // Sprint 4.3.10: transition rule on a step node lands on
  // `PlanDefinition.action.condition` (kind: `applicability`) per the
  // FHIR mapping table in `conditional-logic-and-rules.md`. The CPS
  // rule extension on the condition preserves the rule object verbatim
  // so the round-trip is byte-stable.
  let condition: unknown[] | undefined;
  if (node.transitionRule !== undefined) {
    condition = [ruleToPlanDefinitionCondition(node.transitionRule)];
  }
  const action: PlanDefinitionAction = {
    title: node.label,
    description: node.description,
    textEquivalent: node.id,
    definitionCanonical: `urn:cps:node:${node.id}`,
    code: [makeNodeTypeCoding(node.nodeType)],
    // Top-level extensions on the action.
    // Cast preserves the open-extension passthrough shape.
    ...({ extension: ext } as Record<string, unknown>),
  };
  if (condition !== undefined) {
    action.condition = condition;
  }
  return action;
}

function makeNodeTypeCoding(type: PathwayNodeType): CodeableConcept {
  return {
    coding: [{ system: TYPE_CODING_SYSTEM, code: type }],
  };
}

function readActionExtensions(
  action: PlanDefinitionAction,
): FhirExtension[] {
  const carrier = action as unknown as { extension?: FhirExtension[] };
  return carrier.extension ?? [];
}

/** Convert a FHIR action back to a pathway node. Throws on invalid input. */
function actionToNode(action: PlanDefinitionAction): PathwayNode {
  const id = action.textEquivalent;
  if (!id) {
    throw new Error(
      'PlanDefinition.action missing textEquivalent (pathway node id)',
    );
  }
  const ext = readActionExtensions(action);
  const typeExt = ext.find((e) => e.url === PATHWAY_NODE_TYPE_EXTENSION_URL);
  const rawType = typeExt?.valueCode ?? typeExt?.valueString;
  if (!rawType || !(PATHWAY_NODE_TYPES as readonly string[]).includes(rawType)) {
    throw new Error(
      `PlanDefinition.action "${id}" has unknown node type "${rawType ?? 'absent'}"`,
    );
  }
  const labelExt = ext.find((e) => e.url === PATHWAY_NODE_LABEL_EXTENSION_URL);
  const label = labelExt?.valueString ?? action.title;
  if (!label) {
    throw new Error(`PlanDefinition.action "${id}" missing label`);
  }
  const descExt = ext.find(
    (e) => e.url === PATHWAY_NODE_DESCRIPTION_EXTENSION_URL,
  );
  const reqExt = ext.find(
    (e) => e.url === PATHWAY_NODE_REQUIRES_CONFIRMATION_EXTENSION_URL,
  );
  const stateExt = ext.find(
    (e) => e.url === PATHWAY_NODE_STATE_MACHINE_EXTENSION_URL,
  );
  const linkedExt = ext.find(
    (e) => e.url === PATHWAY_NODE_LINKED_NBAS_EXTENSION_URL,
  );
  const basketExt = ext.find(
    (e) => e.url === PATHWAY_NODE_LAB_BASKET_EXTENSION_URL,
  );
  const node: PathwayNode = {
    id,
    nodeType: rawType as PathwayNodeType,
    label,
  };
  if (descExt?.valueString !== undefined) node.description = descExt.valueString;
  if (reqExt?.valueBoolean !== undefined)
    node.requiresConfirmation = reqExt.valueBoolean;
  if (stateExt?.valueString !== undefined)
    node.stateMachine = stateExt.valueString;
  if (linkedExt?.valueString !== undefined) {
    try {
      const parsed = JSON.parse(linkedExt.valueString);
      if (!Array.isArray(parsed)) {
        throw new Error('linkedNbaIds extension is not an array');
      }
      node.linkedNbaIds = parsed.map((v) => String(v));
    } catch (err) {
      throw new Error(
        `PlanDefinition.action "${id}" linkedNbaIds parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  if (basketExt?.valueString !== undefined) {
    try {
      const parsed = JSON.parse(basketExt.valueString);
      if (!Array.isArray(parsed)) {
        throw new Error('labBasketItems extension is not an array');
      }
      node.labBasketItems = parsed;
    } catch (err) {
      throw new Error(
        `PlanDefinition.action "${id}" labBasketItems parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  // Sprint 4.3.7-fix-2: read hierarchy + step-pill fields back out.
  const parentExt = ext.find(
    (e) => e.url === PATHWAY_NODE_PARENT_EXTENSION_URL,
  );
  if (parentExt?.valueString !== undefined) {
    node.parentNodeId = parentExt.valueString;
  }
  const categoryExt = ext.find(
    (e) => e.url === PATHWAY_NODE_CATEGORY_EXTENSION_URL,
  );
  const categoryRaw = categoryExt?.valueCode ?? categoryExt?.valueString;
  if (categoryRaw === 'user' || categoryRaw === 'system') {
    node.category = categoryRaw;
  }
  const triggerTypeExt = ext.find(
    (e) => e.url === PATHWAY_NODE_TRIGGER_TYPE_EXTENSION_URL,
  );
  if (triggerTypeExt?.valueString !== undefined) {
    node.triggerType = triggerTypeExt.valueString;
  }
  const triggerFilterExt = ext.find(
    (e) => e.url === PATHWAY_NODE_TRIGGER_FILTER_EXTENSION_URL,
  );
  if (triggerFilterExt?.valueString !== undefined) {
    node.triggerFilter = triggerFilterExt.valueString;
  }
  const isDecisionExt = ext.find(
    (e) => e.url === PATHWAY_NODE_IS_DECISION_EXTENSION_URL,
  );
  if (isDecisionExt?.valueBoolean !== undefined) {
    node.isDecision = isDecisionExt.valueBoolean;
  }
  const stepCategoryExt = ext.find(
    (e) => e.url === PATHWAY_NODE_STEP_CATEGORY_EXTENSION_URL,
  );
  const stepCategoryRaw =
    stepCategoryExt?.valueCode ?? stepCategoryExt?.valueString;
  if (stepCategoryRaw !== undefined) {
    node.stepCategory = stepCategoryRaw as PathwayNode['stepCategory'];
  }
  const stepCategoryLabelExt = ext.find(
    (e) => e.url === PATHWAY_NODE_STEP_CATEGORY_LABEL_EXTENSION_URL,
  );
  if (stepCategoryLabelExt?.valueString !== undefined) {
    node.stepCategoryLabel = stepCategoryLabelExt.valueString;
  }
  const programDurationExt = ext.find(
    (e) => e.url === PATHWAY_NODE_PROGRAM_DURATION_EXTENSION_URL,
  );
  if (programDurationExt?.valueString !== undefined) {
    node.programDuration = programDurationExt.valueString;
  }
  const assignedStakeholderExt = ext.find(
    (e) => e.url === PATHWAY_NODE_ASSIGNED_STAKEHOLDER_EXTENSION_URL,
  );
  if (assignedStakeholderExt?.valueString !== undefined) {
    node.assignedStakeholderId = assignedStakeholderExt.valueString;
  }
  // Sprint 4.3.9: read action discriminator + form questions back out.
  const actionTypeExt = ext.find(
    (e) => e.url === PATHWAY_NODE_ACTION_TYPE_EXTENSION_URL,
  );
  const actionTypeRaw =
    actionTypeExt?.valueCode ?? actionTypeExt?.valueString;
  if (actionTypeRaw !== undefined) {
    node.actionType = actionTypeRaw as PathwayNode['actionType'];
  }
  const formQuestionsExt = ext.find(
    (e) => e.url === PATHWAY_NODE_FORM_QUESTIONS_EXTENSION_URL,
  );
  if (formQuestionsExt?.valueString !== undefined) {
    try {
      const parsed = JSON.parse(formQuestionsExt.valueString);
      if (!Array.isArray(parsed)) {
        throw new Error('formQuestions extension is not an array');
      }
      node.formQuestions = parsed as PathwayNode['formQuestions'];
    } catch (err) {
      throw new Error(
        `PlanDefinition.action "${id}" formQuestions parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  // Sprint 4.3.10: read the transition rule back from the FHIR
  // `condition` array. Only the first entry is consulted because the
  // editor authors single-condition rules per sprint scope.
  if (Array.isArray(action.condition) && action.condition.length > 0) {
    try {
      node.transitionRule = planDefinitionConditionToRule(action.condition[0]);
    } catch (err) {
      throw new Error(
        `PlanDefinition.action "${id}" condition parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  return node;
}

/**
 * Convert an `EditablePathway` to a FHIR `PlanDefinition`. The result
 * is ready for `createPlanDefinition`; the FHIR client adds the tenant
 * extension automatically. The mapper still injects the tenant
 * extension so a round-trip without an intermediate write retains
 * tenant scope.
 */
export function pathwayToPlanDefinition(
  pathway: EditablePathway,
): PlanDefinition {
  if (pathway.nodes.length === 0) {
    throw new Error(
      `Cannot map pathway "${pathway.id}" with zero nodes; PlanDefinition.action MUST be non-empty`,
    );
  }
  const actions = pathway.nodes.map(nodeToAction);
  const edgesJson = JSON.stringify(pathway.edges);
  const extension: FhirExtension[] = [
    { url: TENANT_EXTENSION_URL, valueString: pathway.tenantId },
    { url: PATHWAY_ID_EXTENSION_URL, valueString: pathway.pathwayId },
    { url: PATHWAY_EDGES_EXTENSION_URL, valueString: edgesJson },
  ];
  return {
    resourceType: 'PlanDefinition',
    id: pathway.id,
    name: pathway.id,
    title: pathway.pathwayId,
    status: 'active',
    type: {
      coding: [{ system: TYPE_CODING_SYSTEM, code: 'care-pathway' }],
    },
    action: actions,
    extension,
  };
}

/** Convert a FHIR `PlanDefinition` back to an `EditablePathway`. */
export function planDefinitionToPathway(
  resource: PlanDefinition,
): EditablePathway {
  if (!resource.id) {
    throw new Error('PlanDefinition missing id');
  }
  if (!Array.isArray(resource.action) || resource.action.length === 0) {
    throw new Error(
      `PlanDefinition "${resource.id}" missing action (pathway has no nodes)`,
    );
  }
  const ext = resource.extension ?? [];
  const tenantExt = ext.find((e) => e.url === TENANT_EXTENSION_URL);
  const tenantId = tenantExt?.valueString;
  if (!tenantId) {
    throw new Error(
      `PlanDefinition "${resource.id}" missing tenant-id extension`,
    );
  }
  const pathwayIdExt = ext.find((e) => e.url === PATHWAY_ID_EXTENSION_URL);
  const pathwayIdRaw = pathwayIdExt?.valueString;
  if (
    !pathwayIdRaw ||
    !(PATHWAY_IDS as readonly string[]).includes(pathwayIdRaw)
  ) {
    throw new Error(
      `PlanDefinition "${resource.id}" missing or invalid pathway-id extension: "${pathwayIdRaw ?? 'absent'}"`,
    );
  }
  const pathwayId = pathwayIdRaw as PathwayId;
  if (resource.id !== pathwayIdFor(pathwayId)) {
    throw new Error(
      `PlanDefinition id "${resource.id}" does not match pathway-id "${pathwayId}" (expected "${pathwayIdFor(pathwayId)}")`,
    );
  }
  const nodes = resource.action.map(actionToNode);
  const edgesExt = ext.find((e) => e.url === PATHWAY_EDGES_EXTENSION_URL);
  let edges: PathwayEdge[] = [];
  if (edgesExt?.valueString !== undefined) {
    try {
      const parsed = JSON.parse(edgesExt.valueString);
      if (!Array.isArray(parsed)) {
        throw new Error('edges extension is not an array');
      }
      edges = parsed as PathwayEdge[];
    } catch (err) {
      throw new Error(
        `PlanDefinition "${resource.id}" edges parse failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  return {
    id: resource.id,
    pathwayId,
    tenantId,
    nodes,
    edges,
  };
}
